import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Droplets, Facebook, MapPin, Wind } from "lucide-react";
import { FACEBOOK_URL } from "../site";

const HERO_IMG =
  "https://images.unsplash.com/photo-1776673688060-68433e0f7e9b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHw0fHxjbGVhbiUyMGZhYnJpYyUyMHNvZmElMjBsaXZpbmclMjByb29tJTIwcHJpc3RpbmUlMjBpbnRlcmlvcnxlbnwwfHx8fDE3OTE3MTcyMjJ8MA&ixlib=rb-4.1.0&q=85";

const lines = ["Canapés, Matelas,", "Moquettes & Auto —", "Comme Neufs."];

const pills = [
  { icon: MapPin, label: "Intervention à domicile" },
  { icon: Droplets, label: "Injection-extraction pro" },
  { icon: Wind, label: "Séchage en 2h – 4h" },
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  return (
    <section id="accueil" ref={ref} className="relative overflow-hidden pt-32 lg:pt-40">
      <div
        className="pointer-events-none absolute -top-32 right-[-10%] h-[34rem] w-[34rem] rounded-full bg-teal-100/70 blur-3xl"
        aria-hidden="true"
      />
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-4 pb-20 sm:px-6 lg:grid-cols-12 lg:gap-8 lg:px-8">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mb-6 text-xs font-semibold uppercase tracking-[0.25em] text-teal-700 sm:text-sm"
          >
            PureTextiles — Nettoyage textile professionnel à domicile
          </motion.p>

          <h1
            data-testid="hero-title"
            className="font-serif text-4xl font-medium leading-[1.08] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl"
          >
            {lines.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  initial={{ y: "115%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.2 + i * 0.16, ease: [0.22, 1, 0.36, 1] }}
                >
                  {i === 1 ? (
                    <>
                      Moquettes <span className="italic text-teal-600">&amp;</span> Auto —
                    </>
                  ) : (
                    line
                  )}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            data-testid="hero-subtitle"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.75 }}
            className="mt-6 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg"
          >
            Canapé, matelas, sommier, moquette du salon ou habitacle de votre
            voiture : nous redonnons à vos textiles leur fraîcheur d'origine,
            directement chez vous. Tarifs affichés, sans surprise.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.9 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <a
              href="#tarifs"
              data-testid="hero-cta-tarifs"
              className="group inline-flex items-center gap-2 rounded-full bg-slate-900 px-7 py-3.5 text-sm font-semibold text-white transition-all duration-200 hover:-translate-y-0.5 hover:bg-teal-700 hover:shadow-xl"
            >
              Découvrir nos tarifs
              <ArrowDown className="h-4 w-4 transition-transform duration-200 group-hover:translate-y-0.5" />
            </a>
            <a
              href={FACEBOOK_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-facebook"
              className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white/70 px-7 py-3.5 text-sm font-semibold text-slate-800 backdrop-blur transition-all duration-200 hover:-translate-y-0.5 hover:border-[#1877F2] hover:text-[#1877F2] hover:shadow-lg"
            >
              <Facebook className="h-4 w-4" />
              Écrivez-nous sur Facebook
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.1 }}
            className="mt-10 flex flex-wrap gap-3"
          >
            {pills.map(({ icon: Icon, label }) => (
              <span
                key={label}
                className="inline-flex items-center gap-2 rounded-full border border-teal-100 bg-teal-50 px-4 py-2 text-xs font-semibold text-teal-800 sm:text-sm"
              >
                <Icon className="h-4 w-4" />
                {label}
              </span>
            ))}
          </motion.div>
        </div>

        <div className="relative lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="overflow-hidden rounded-[2.5rem] rounded-tr-[6rem] shadow-2xl shadow-slate-900/20">
              <motion.img
                src={HERO_IMG}
                alt="Canapé en tissu impeccable après nettoyage professionnel"
                data-testid="hero-image"
                style={{ y: imgY, scale: 1.15 }}
                className="h-[26rem] w-full object-cover sm:h-[30rem]"
              />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-slate-900/40 via-transparent to-transparent" />
            </div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 1.15 }}
              className="absolute -bottom-6 -left-4 rounded-2xl border border-slate-100 bg-white/90 px-6 py-4 shadow-xl backdrop-blur sm:-left-10"
            >
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                Canapé 2 places, nettoyage complet
              </p>
              <p className="mt-1 font-serif text-3xl font-semibold text-slate-900">
                dès 65,00 €
              </p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
